'use client';
import {
  EnhancedChainData,
  ExpiryDates,
  OptionChainFilterProps,
  OrganizeOptionDataReturn,
  Results,
} from '@benzinga/data-option-chain';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getfilteredData, getFormattedDataByExpiry, getStrikeRangeFromChain } from '@benzinga/frontend-utils';

type useOptionChainParams = {
  optionChainData: OrganizeOptionDataReturn;
  step?: number;
  stockPrice: number;
  symbol: string;
};

export type returnTypeOfUseOptionChain = {
  expiries: ExpiryDates[];
  initialRange: [number, number];
  filteredData: Results[];
  moneyness: NonNullable<'all' | 'near-the-money' | undefined>;
  onExpiryChange: (date: string | number) => void;
  onMoneynessChange: <T = NonNullable<OptionChainFilterProps['moneyNess']>>(value: T) => void;
  onRangeChange: (to: number, from: number) => void;
  range: [number, number];
  rangeLimits: [number, number];
};

export const useOptionChain = ({
  optionChainData,
  step = 5,
  stockPrice,
  symbol,
}: useOptionChainParams): returnTypeOfUseOptionChain => {
  const { dataByExpiry, expiries } = optionChainData || {};
  const [moneyness, setMoneyness] = useState<NonNullable<OptionChainFilterProps['moneyNess']>>('all');

  const formattedData = useMemo(
    () => getFormattedDataByExpiry({ dataByExpiry, stockPrice, symbol }),
    [dataByExpiry, stockPrice, symbol],
  );

  const validExpiries = useMemo(() => {
    if (!expiries || !formattedData) return [];
    return expiries.filter(expiry => {
      const expiryData = formattedData[expiry.value] as EnhancedChainData;
      return expiryData?.chains && expiryData.chains.length > 0;
    });
  }, [expiries, formattedData]);

  const initialExpiry = useMemo(() => validExpiries?.[0]?.value, [validExpiries]);
  const [_expiry, setExpiry] = useState('');

  const expiry = _expiry || initialExpiry;

  const selectedExpiryData = useMemo(() => {
    if (expiry && formattedData?.[expiry]) {
      return formattedData[expiry] as EnhancedChainData;
    }

    return {} as EnhancedChainData;
  }, [formattedData, expiry]);

  const initialRange = useMemo(() => {
    if (step) {
      const rawRange = getStrikeRangeFromChain(selectedExpiryData);
      const [min, max] = rawRange;

      const stepsFromMin = Math.floor((max - min) / step);
      const adjustedMax = min + stepsFromMin * step;

      return [min, adjustedMax] as [number, number];
    }
    return getStrikeRangeFromChain(selectedExpiryData);
  }, [selectedExpiryData, step]);

  const initialRangeString = JSON.stringify(initialRange);

  const [range, setRange] = useState(initialRange);

  useEffect(() => {
    setRange(JSON.parse(initialRangeString) as [number, number]);
  }, [expiry, symbol, initialRangeString]);

  const filteredData = useMemo(() => {
    return getfilteredData({ data: selectedExpiryData, moneyness, range, stockPrice });
  }, [selectedExpiryData, moneyness, range, stockPrice]);

  const onExpiryChange = useCallback(
    (date: string | number) => {
      setExpiry(date as string);

      const newSelectedExpiryData = formattedData?.[date as string] as EnhancedChainData;

      if (newSelectedExpiryData?.chains?.length) {
        setRange(getStrikeRangeFromChain(newSelectedExpiryData));
      } else {
        setRange([0, 500]);
      }
    },
    [formattedData],
  );

  const onRangeChange = useCallback((to: number, from: number) => {
    setRange([to, from]);
  }, []);

  const onMoneynessChange: OptionChainFilterProps['onMoneynessChange'] = useCallback(value => {
    setMoneyness(value as NonNullable<OptionChainFilterProps['moneyNess']>);
  }, []);

  return {
    expiries: validExpiries,
    filteredData,
    initialRange,
    moneyness,
    onExpiryChange,
    onMoneynessChange,
    onRangeChange,
    range,
    rangeLimits: initialRange,
  };
};
